import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonContent, Platform, PopoverController } from '@ionic/angular';
import { PopoverSocialShareComponent } from 'src/app/components/popover-social-share/popover-social-share.component';
import { SelectListComponent } from 'src/app/components/select-list/select-list.component';
import { GlobalDataService } from 'src/app/services/global-data.service';
import { SeoService } from 'src/app/services/seo.service';
import { CategoryItem } from 'src/app/types/category';
import {
  CinemaDay,
  CinemaDaySlot,
  CinemaProvider,
  CinemaTimeSlot,
  CinemaType,
  Movie,
} from 'src/app/types/movie';
import { SEOSchema } from 'src/app/types/seo';
import { environment } from 'src/environments/environment';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-movie-detail',
  templateUrl: './movie-detail.page.html',
  styleUrls: ['./movie-detail.page.scss'],
})
export class MovieDetailPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  desktop: boolean = false;
  tablet: boolean = false;
  title: string;
  trailer: string;
  skBookingLink: string;
  bookingError: string;
  metaKeywords: string;
  bookNow: boolean = false;

  selectedCinemaType: CategoryItem;
  selectedCinemaDay: CinemaDay = null;
  selectedCinemaTime: CinemaTimeSlot;

  cinemaProvider: CinemaProvider;
  cinemaTimes: CinemaTimeSlot[] = [];
  cinemaTypes: CinemaType[] = [];
  daySlots: CinemaDaySlot[] = [];
  timeSlots: string[] = [];
  mallSessions = [];
  sessions = [];
  nowShowing: Movie[] = [];
  comingSoon: Movie[] = [];
  sortOrder = ['4dx', 'Xtreme', 'VIP 2D', '3D', '2D'];

  moviesPageData;

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'Movie',
    author: {
      '@type': 'Organization',
      name: environment.environment_name,
    },
    publisher: {
      '@type': 'Organization',
      name: environment.environment_name,
    },
  };

  slideOpts = {
    freeMode: false,
    pagination: false,
    breakpoints: {
      280: {
        slidesPerView: 2.2,
        spaceBetween: 15,
      },
      768: {
        slidesPerView: 'auto',
        spaceBetween: 30,
      },
    },
  };

  constructor(
    private platform: Platform,
    private globalData: GlobalDataService,
    private seo: SeoService,
    private route: ActivatedRoute,
    public popoverController: PopoverController
  ) {
    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
      this.tablet = this.platform.is('tablet');
    });

    this.platform.resize.subscribe(async () => {
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
    });
  }

  /**
   * Present social share popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: PopoverSocialShareComponent,
      event: ev,
      translucent: true,
      cssClass: 'social-popover',
    });
    return await popover.present();
  }

  /**
   * Present cinema type select popover
   */
  async presentCinemaTypePopover(ev: any) {
    const popover = await this.popoverController.create({
      component: SelectListComponent,
      componentProps: {
        data: this.cinemaTypes,
        hideReset: true,
      },
      event: ev,
      translucent: true,
      cssClass: 'cinema-popover',
    });

    popover.onWillDismiss().then((popoverData) => {
      const selectedCinemaType = {
        title: popoverData.data?.title,
        slug: popoverData.data?.slug,
      };

      if (popoverData.data && this.selectedCinemaType != selectedCinemaType) {
        this.setCinemaSessions(selectedCinemaType);
      }
    });

    return await popover.present();
  }

  /**
   * Build and return Nu Metro booking link
   */
  nuMetroBooking() {
    if (!this.selectedCinemaTime) {
      this.bookingError = 'Please select the screening time you wish to book';
      return;
    }

    this.bookingError = null;

    const currentSession = this.moviesPageData.sessions.filter((session) => {
      return (
        new Date(session.session_date).toDateString() ===
          this.selectedCinemaDay.label &&
        session.session_time.includes(this.selectedCinemaTime) &&
        session.movie_id === this.moviesPageData.movie.id
      );
    });

    if (currentSession[0] && typeof window !== 'undefined')
      window.open(currentSession[0].session_link, '_blank');
  }

  /**
   * Change selected day for bookings
   */
  selectCinemaDay(day) {
    this.selectedCinemaDay = day;
    this.timeSlots = [];

    if (this.cinemaProvider.slug === 'numetro') {
      this.mallSessions.map((session) => {
        if (new Date(session.session_date).toDateString() === day.label) {
          if (
            (this.selectedCinemaType.title === '3D' &&
              session.is_3d === '1' &&
              session.is_vip !== '1' &&
              session.is_xtreme !== '1' &&
              session.is_4dx !== '1') ||
            (this.selectedCinemaType.title === '4DX' &&
              session.is_4dx === '1') ||
            (this.selectedCinemaType.title === 'VIP | 2D' &&
              session.is_vip === '1' &&
              session.is_2d === '1') ||
            (this.selectedCinemaType.title === 'VIP | 3D' &&
              session.is_vip === '1' &&
              session.is_3d === '1') ||
            (this.selectedCinemaType.title === 'Scene Xtreme | 2D' &&
              session.is_xtreme === '1' &&
              session.is_2d === '1') ||
            (this.selectedCinemaType.title === 'Scene Xtreme | 3D' &&
              session.is_xtreme === '1' &&
              session.is_3d === '1') ||
            (this.selectedCinemaType.title === '2D' &&
              session.is_2d === '1' &&
              session.is_vip !== '1' &&
              session.is_xtreme !== '1' &&
              session.is_4dx !== '1')
          ) {
            const timeSlotArray = session.session_time.split(':');
            const timeSlot = timeSlotArray
              .slice(0, timeSlotArray.length - 1)
              .join(':');
            if (!this.timeSlots.includes(timeSlot))
              this.timeSlots.push(timeSlot);
          }
        }
      });
    } else {
      this.mallSessions.map((session) => {
        if (
          session.experiences.includes(this.selectedCinemaType.title) &&
          new Date(session.show_time * 1000).getDate() +
            '-' +
            new Date(session.show_time * 1000).getMonth() ===
            new Date(day.date * 1000).getDate() +
              '-' +
              new Date(day.date * 1000).getMonth()
        ) {
          if (!this.timeSlots.includes(session.time_tag))
            this.timeSlots.push(session.time_tag);
        }
      });

      this.setSkBookingLink();
    }

    this.timeSlots.sort();
  }

  /**
   * Change selected time for bookings
   */
  selectCinemaTime(time) {
    this.selectedCinemaTime = time;
    this.bookNow = true;
  }

  /**
   * Change available slots according to time, date and type
   */
  setCinemaSlots() {
    // Day & time slots
    this.daySlots = [];

    if (this.cinemaProvider.slug === 'numetro') {
      this.moviesPageData.sessions.map((session) => {
        if (
          session.movie_id === this.moviesPageData.movie.id &&
          ((this.selectedCinemaType.title === '3D' &&
            session.is_3d === '1' &&
            session.is_vip !== '1' &&
            session.is_xtreme !== '1' &&
            session.is_4dx !== '1') ||
            (this.selectedCinemaType.title === '4DX' &&
              session.is_4dx === '1') ||
            (this.selectedCinemaType.title === 'VIP | 2D' &&
              session.is_vip === '1' &&
              session.is_2d === '1') ||
            (this.selectedCinemaType.title === 'VIP | 3D' &&
              session.is_vip === '1' &&
              session.is_3d === '1') ||
            (this.selectedCinemaType.title === 'Scene Xtreme | 2D' &&
              session.is_xtreme === '1' &&
              session.is_2d === '1') ||
            (this.selectedCinemaType.title === 'Scene Xtreme | 3D' &&
              session.is_xtreme === '1' &&
              session.is_3d === '1') ||
            (this.selectedCinemaType.title === '2D' &&
              session.is_2d === '1' &&
              session.is_vip !== '1' &&
              session.is_xtreme !== '1' &&
              session.is_4dx !== '1'))
        ) {
          this.mallSessions.push(session);

          let date = {
            label: new Date(session.session_date).toDateString(),
            date: session.session_date,
          };

          if (
            this.daySlots.filter(function (e) {
              return e.label === date.label;
            }).length < 1
          )
            this.daySlots.push(date);
        }
      });
    } else {
      Object.keys(this.moviesPageData.sessions).map((key, index) => {
        this.moviesPageData.sessions[key].map((session) => {
          if (
            session.cinema_id === this.cinemaProvider.cineplexId.toString() &&
            session.experiences.includes(this.selectedCinemaType.title)
          ) {
            this.mallSessions.push(session);

            let date = {
              label: new Date(session.show_time * 1000).toDateString(),
              date: session.show_time,
            };

            if (
              this.daySlots.filter(function (e) {
                return e.label === date.label;
              }).length < 1
            )
              this.daySlots.push(date);
          }
        });
      });
    }

    if (this.daySlots && this.daySlots[0])
      this.daySlots.sort((a, b) => (a.date > b.date ? 1 : -1));

    // When changing cinema type, select same day slot if available in new cinema type
    const relatedCinemaDay =
      this.selectedCinemaDay &&
      this.daySlots.filter(
        (slot) => slot.label === this.selectedCinemaDay.label
      );

    if (this.selectedCinemaDay && relatedCinemaDay[0]) {
      this.selectCinemaDay(relatedCinemaDay[0]);
    } else {
      this.selectCinemaDay(this.daySlots[0]);
    }
  }

  /**
   * Set cimema session options
   */
  setCinemaSessions(cinemaType) {
    this.selectedCinemaType = cinemaType;
    this.setCinemaSlots();
  }

  /**
   * Set cinema type options
   */
  setCinemaTypes() {
    let availableExperiences = [];
    this.cinemaTypes = [];

    if (this.cinemaProvider.slug === 'numetro') {
      this.moviesPageData.sessions.map((session) => {
        if (session.movie_id === this.moviesPageData.movie.id) {
          if (session.is_vip === '1') {
            if (
              session.is_2d === '1' &&
              !availableExperiences.includes('VIP | 2D')
            ) {
              availableExperiences.push('VIP | 2D');
            }
            if (
              session.is_3d === '1' &&
              !availableExperiences.includes('VIP | 3D')
            ) {
              availableExperiences.push('VIP | 3D');
            }
          } else if (session.is_xtreme === '1') {
            if (
              session.is_2d === '1' &&
              !availableExperiences.includes('Scene Xtreme | 2D')
            ) {
              availableExperiences.push('Scene Xtreme | 2D');
            }
            if (
              session.is_3d === '1' &&
              !availableExperiences.includes('Scene Xtreme | 3D')
            ) {
              availableExperiences.push('Scene Xtreme | 3D');
            }
          } else if (
            session.is_4dx === '1' &&
            !availableExperiences.includes('4DX')
          ) {
            availableExperiences.push('4DX');
          } else if (
            session.is_vip !== '1' &&
            session.is_xtreme !== '1' &&
            session.is_3d === '1' &&
            !availableExperiences.includes('3D')
          ) {
            availableExperiences.push('3D');
          } else if (
            session.is_vip !== '1' &&
            session.is_xtreme !== '1' &&
            session.is_2d === '1' &&
            !availableExperiences.includes('2D')
          ) {
            availableExperiences.push('2D');
          }
        }
      });
    } else {
      Object.keys(this.moviesPageData.sessions).map((key, index) => {
        this.moviesPageData.sessions[key].map((session) => {
          if (session.cinema_id === this.cinemaProvider.cineplexId.toString()) {
            session.experiences.map((experience) => {
              if (!availableExperiences.includes(experience))
                availableExperiences.push(experience);
            });
          }
        });
      });
    }

    availableExperiences.sort((a, b) => {
      return this.sortOrder.indexOf(a) - this.sortOrder.indexOf(b);
    });

    for (let index = 0; index < availableExperiences.length; index++) {
      this.cinemaTypes.push({
        title: availableExperiences[index],
        slug: availableExperiences[index]
          .trim()
          .toLowerCase()
          .replaceAll(' ', '_'),
      });
    }

    this.setCinemaSessions(this.cinemaTypes[0]);
  }

  /**
   * Set SK booking link
   */
  setSkBookingLink() {
    if (this.cinemaProvider.slug === 'sterkinekor') {
      let cinemaName;
      switch (this.cinemaProvider.cineplexId) {
        case 6031:
          cinemaName = 'capegate';
          break;

        case 6121:
          cinemaName = 'somerset';
          break;

        default:
          cinemaName = 'rosebank-nouveau';
          break;
      }
      
      this.skBookingLink = `https://movies.sterkinekor.com/f/${this.moviesPageData.movie.title_slug}/${this.moviesPageData.movie.id}?location=${cinemaName}`;
      if (this.selectedCinemaDay) this.skBookingLink = this.skBookingLink + `&date=${new DatePipe('en-US').transform(this.selectedCinemaDay.label, 'yyyy-MM-dd')}`;
    }
  }

  /**
   * Set cinema provider
   */
  setCinemaProvider(provider, movieDetail) {
    this.cinemaProvider = provider;
    this.title = this.cinemaProvider.name + ' Movies';
    this.moviesPageData = {
      movie: {
        ...movieDetail,
        favorited: false,
      },
      sessions:
        this.cinemaProvider.slug === 'numetro'
          ? this.sessions
          : this.sessions[movieDetail.id],
      relatedContent: [
        ...this.nowShowing.filter((movie) => movie.id !== movieDetail.id),
      ],
    };

    if (this.moviesPageData?.movie?.youtube_id) {
      this.trailer = `<iframe src="https://www.youtube.com/embed/${this.moviesPageData.movie.youtube_id}" frameborder="0" ></iframe>`;
    } else if (this.moviesPageData?.movie?.vimeo_id) {
      this.trailer = `<iframe src="https://player.vimeo.com/video/${this.moviesPageData.movie.vimeo_id}" frameborder="0" ></iframe>`;
    }

    this.setSkBookingLink();

    if (!this.moviesPageData?.movie?.comingSoon) this.setCinemaTypes();

    if (!this.cordova) {
      // Title tag
      const seoTitle =
        provider.name +
        ' Movie | ' +
        this.moviesPageData.movie.title +
        ' | ' +
        environment.environment_title +
        ' Shopping Mall';
      this.seo.setTitle(seoTitle);

      // Description tag
      const seoData = {
        title: seoTitle,
        description: this.moviesPageData.movie.synopsis_short
          ? this.moviesPageData.movie.synopsis_short.slice(0, 160)
          : this.moviesPageData.movie.synopsis
          ? this.moviesPageData.movie.synopsis.slice(0, 160)
          : '',
        keywords:
          this.metaKeywords +
          ' movies ' +
          this.moviesPageData.movie.title +
          ' ' +
          provider.name,
        socialImage: this.moviesPageData.movie.poster_file,
      };
      this.seo.setMeta(seoData);
      this.seo.setSEOMeta(seoData);
    }
  }

  ngOnInit() {
    const initialMallData = this.globalData.getGlobalData();
    this.metaKeywords = initialMallData.general.metaKeywords;

    this.route.data.subscribe((response) => {
      if (response?.data?.nowShowing) {
        this.nowShowing = response.data.nowShowing;
        this.comingSoon = response.data.comingSoon;
        this.sessions = response.data.sessions;
        this.setCinemaProvider(
          response.data.cinemaProvider,
          response.data.movieDetail
        );
      }
    });
  }

  ionViewWillLeave() {
    if (document != undefined) {
      document
        .querySelectorAll('.iframe-wrapper-inner iframe')
        .forEach((iframe: HTMLIFrameElement) => {
          var iframeSrc = iframe.src;
          iframe.src = iframeSrc;
        });
    }
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }
}
